import type {
  Leniency,
  PanelCell,
  PanelCellState,
  PanelEntry,
  PanelEntryStatus,
  PanelHistoryItem,
  PanelHistoryKind,
  PanelJudge,
  ScoresHiddenReason,
} from '../types';

/**
 * The Panel 360 page's pure helpers (#41, spec §Web "Pure lib").
 *
 * The server builds the overview — cells, counts, sort and every score rule
 * (D7) — so nothing here decides what a reader may see. These only filter,
 * group and point at what the server sent.
 *
 * The tuples are the closed vocabularies the page translates
 * (`judgingSetup.panel360.cellState.<x>`, `…entryStatus.<x>`, `…leniency.<x>`,
 * `…history.kind.<x>`, `…notes.<x>`), registered in `dynamic-keys.test.ts` so
 * every value carries EN and RW copy.
 */
export const PANEL_CELL_STATES = [
  'SCORED',
  'DRAFT',
  'NOT_STARTED',
  'NOT_SENT',
  'CONFLICT',
  'RECUSED',
  'NOT_SCORED',
  'NONE',
  'KEPT',
  'HANDED_OVER',
] as const satisfies readonly PanelCellState[];

export const PANEL_ENTRY_STATUSES = [
  'BLOCKED',
  'MISSING',
  'NOT_FINISHED',
  'FULLY_SCORED',
] as const satisfies readonly PanelEntryStatus[];

export const LENIENCY_BANDS = [
  'MUCH_LOWER',
  'A_LITTLE_LOWER',
  'IN_LINE',
  'A_LITTLE_HIGHER',
  'MUCH_HIGHER',
] as const satisfies readonly Leniency[];

export const HISTORY_KINDS = [
  'PANEL_CREATED',
  'PANEL_RENAMED',
  'JUDGE_ADDED',
  'JUDGES_COPIED',
  'JUDGE_REMOVED',
  'CHAIR_SET',
  'ENTRIES_ALLOCATED',
  'JUDGING_STARTED',
  'WORK_SENT',
  'CONFLICT_DECLARED',
  'CONFLICT_WITHDRAWN',
  'CONFLICT_DECIDED',
  'JUDGE_ADDED_LATE',
  'JUDGE_REPLACED',
  'SUBSTITUTE_ASSIGNED',
  'SUBSTITUTE_DECLINED',
  'ENTRY_MOVED_OUT',
  'ENTRY_MOVED_IN',
] as const satisfies readonly PanelHistoryKind[];

export const SCORE_HIDDEN_REASONS = ['CHAIR', 'ROUND_OPEN', 'NO_PERMISSION'] as const satisfies readonly ScoresHiddenReason[];

/** The grid's filters (`?filter=`). Which ones a reader is offered depends on the round and D7. */
export const PANEL_FILTERS = ['all', 'unfinished', 'blocked', 'disagree'] as const;
export type PanelFilter = (typeof PANEL_FILTERS)[number];

/**
 * The entries one filter keeps, in the server's needs-attention order.
 *
 * "Not finished" is every entry not fully scored — blocked and (after close)
 * missing ones included, as the mockups count them. `disagree` is null while
 * scores are hidden, and null is never a disagreement.
 */
export function filterEntries(entries: readonly PanelEntry[], filter: PanelFilter): PanelEntry[] {
  switch (filter) {
    case 'all':
      return [...entries];
    case 'unfinished':
      return entries.filter((e) => e.status !== 'FULLY_SCORED');
    case 'blocked':
      return entries.filter((e) => e.status === 'BLOCKED');
    case 'disagree':
      return entries.filter((e) => e.disagree === true);
  }
}

/**
 * Who "Remind its judges" emails: every member still owing work they can do
 * (owed − submitted − conflictsWaiting > 0). A judge whose only open work is a
 * conflict waiting for a decision is left out: the email would ask for a score
 * they may not give (coordinator decision, work-log G8; same rule as the
 * per-judge `hasWorkToRemind`). The server skips anyone reminded in the last
 * 12 hours, so this does not filter on it.
 */
export function remindTargets(judges: readonly PanelJudge[]): string[] {
  // A replaced judge's column is a record, never a reminder target (#39 D1).
  return judges.filter((j) => j.kind !== 'REPLACED' && j.owed - j.submitted - j.conflictsWaiting > 0).map((j) => j.evaluatorId);
}

/** How long a judge stays un-remindable after a reminder (the server's window). */
export const REMIND_WINDOW_HOURS = 12;
const HOUR_MS = 3_600_000;

/**
 * Whole hours until a judge can be reminded again ("can be reminded again in
 * N h"), rounded up so the last minutes read as 1 h, never 0. Null when they
 * can be reminded now, or never were.
 */
export function remindWindow(lastRemindedAt: string | null, now: Date): number | null {
  if (lastRemindedAt === null) return null;
  const left = Date.parse(lastRemindedAt) + REMIND_WINDOW_HOURS * HOUR_MS - now.getTime();
  if (!Number.isFinite(left) || left <= 0) return null;
  return Math.min(REMIND_WINDOW_HOURS, Math.ceil(left / HOUR_MS));
}

/** One day of the History tab: `day` is the Kigali (CAT) calendar date, `YYYY-MM-DD`. */
export interface HistoryDay {
  day: string;
  items: PanelHistoryItem[];
}

// en-CA formats as YYYY-MM-DD. Kigali keeps CAT (UTC+2) all year, so a day
// boundary here is the one the timeline's CAT times are read against.
const CAT_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Africa/Kigali',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * The loaded history, grouped by day in CAT, newest day first. Items keep the
 * server's order inside a day (newest first by audit seq), so concatenated
 * pages continue the day a previous page ended on rather than repeating it.
 */
export function historyByDay(items: readonly PanelHistoryItem[]): HistoryDay[] {
  const byDay = new Map<string, PanelHistoryItem[]>();
  for (const item of items) {
    const day = CAT_DAY.format(new Date(item.at));
    const group = byDay.get(day);
    if (group) group.push(item);
    else byDay.set(day, [item]);
  }
  return [...byDay.entries()]
    .sort(([a], [b]) => (a < b ? 1 : a > b ? -1 : 0))
    .map(([day, dayItems]) => ({ day, items: dayItems }));
}

/** Cells that put no judge on the entry: no row at all, a draft task never sent, or work handed to a replacement. */
const NOT_ON_ENTRY: ReadonlySet<PanelCellState> = new Set(['NONE', 'NOT_SENT', 'HANDED_OVER']);

/**
 * The score sheet's "Other judges on this entry" line, built from the overview
 * already on screen (no second read): every other judge holding the entry, in
 * panel order, recused ones included.
 */
export function otherJudgesOn(entry: PanelEntry, evaluatorId: string): PanelCell[] {
  return entry.cells.filter((c) => c.evaluatorId !== evaluatorId && !NOT_ON_ENTRY.has(c.state));
}

/**
 * A judge's name by account id, from the panel's columns (members and
 * replaced judges), compared case-insensitively; null when not a column here
 * or nameless. The move dialog and the Substitutes cell name people with it.
 */
export function judgeNameLookup(judges: readonly Pick<PanelJudge, 'evaluatorId' | 'displayName'>[]): (evaluatorId: string) => string | null {
  const known = new Map<string, string>();
  for (const j of judges) if (j.displayName) known.set(j.evaluatorId.toLowerCase(), j.displayName);
  return (id) => known.get(id.toLowerCase()) ?? null;
}

/**
 * The Panel 360 address. `asChair` opens the chair's progress-only view
 * (`?as=chair`), which reads the chair route instead of the staff one.
 */
export function panel360Href(
  awardId: string,
  stageId: string,
  panelId: string,
  { asChair = false }: { asChair?: boolean } = {},
): string {
  const path = `/awards/${encodeURIComponent(awardId)}/rounds/${encodeURIComponent(stageId)}/panels/${encodeURIComponent(panelId)}`;
  return asChair ? `${path}?as=chair` : path;
}
